import React, { ReactNode } from "react";
import { ScanLine } from "lucide-react";
import CollectionIcon from "modules/common/components/Icons/CollectionIcon";
import DecksIcon from "modules/common/components/Icons/DecksIcon";
import MatchedCardsIcon from "modules/common/components/Icons/MatchedCardsIcon";
import { ShortLGS } from "modules/common/model/models";

const PILLAR_ICON_PX = 14;

/**
 * Single source of truth for every word that ships in <VisitorFunnelCard>.
 * Headline / eyebrow / pillars / CTAs are constant across surfaces; the
 * lede flips on (kind × direction). Edit here — the component just renders.
 */

export type FunnelDirection = "haves" | "wants";

export type FunnelContext = { direction: FunnelDirection } & (
  | {
      kind: "list";
      ownerName: string;
      stores: ShortLGS[];
      /** Owner avatar for the mobile bar's identity slot. */
      preferredAvatar?: string;
    }
  | { kind: "city"; cityName: string }
  | { kind: "store"; storeName: string }
  | { kind: "user"; username: string; preferredAvatar?: string }
  | { kind: "set"; setName: string }
);

export interface FunnelPillar {
  /** Pre-rendered icon node so the copy file can mix lucide and custom
   *  brand icons (which have different size APIs) without an adapter. */
  icon: ReactNode;
  title: string;
  description: string;
}

export const FUNNEL_EYEBROW = "Trade locally on Lootstack";

/**
 * Rendered with the second word in primary color: "Want any of these?".
 * Split so the JSX can wrap `emphasized` in <em> without string parsing.
 */
export const FUNNEL_HEADLINE = {
  before: "Want any of ",
  emphasized: "these",
  after: "?",
};

export const FUNNEL_PILLARS: FunnelPillar[] = [
  {
    icon: <MatchedCardsIcon fontSize={PILLAR_ICON_PX} />,
    title: "Match trades",
    description:
      "We pair your wants with binders like this at stores you both visit.",
  },
  {
    icon: <ScanLine size={PILLAR_ICON_PX} />,
    title: "Scan your binder",
    description: "Point your camera at a stack, listed in seconds.",
  },
  {
    icon: <CollectionIcon fontSize={PILLAR_ICON_PX} />,
    title: "Track your collection",
    description: "Know what you own and what it's worth.",
  },
  {
    icon: <DecksIcon fontSize={PILLAR_ICON_PX} />,
    title: "Build decks",
    description: "Brew, then trade for the pieces you're missing.",
  },
];

export const FUNNEL_CTA = {
  primary: "Start trading for free",
  signInPrompt: "Already on Lootstack? ",
  signInLink: "Sign in",
};

function formatStoreList(stores: ShortLGS[]): string {
  const [first, second, ...rest] = stores;
  if (stores.length === 1) return first.name;
  if (rest.length > 0)
    return `${first.name}, ${second.name} and ${rest.length} more`;
  return `${first.name} and ${second.name}`;
}

/**
 * Per-(kind × direction) lede sentence. Haves pitch lands "find what you
 * want"; wants pitch lands "fill needs you can supply".
 */
export function buildFunnelLede(context: FunnelContext): string {
  const isHaves = context.direction === "haves";

  switch (context.kind) {
    case "list": {
      const where =
        context.stores.length > 0
          ? ` at ${formatStoreList(context.stores)}`
          : "";
      return isHaves
        ? `Up for trade${where}. Tell us what you're after and we'll match you with ${context.ownerName} and other players who trade there.`
        : `Cards ${context.ownerName} is looking for${where}. List the ones you have and we'll connect you.`;
    }
    case "city":
      return isHaves
        ? `Cards being traded by players in ${context.cityName} right now. Tell us what you're after and we'll match you with traders nearby.`
        : `Cards players in ${context.cityName} are looking for. List the ones you have and we'll match you with them.`;
    case "store":
      return isHaves
        ? `Cards on offer at ${context.storeName}. Tell us what you're after and we'll match you with traders there.`
        : `Cards players at ${context.storeName} are looking for. List the ones you have and we'll match you with them.`;
    case "user":
      return isHaves
        ? `Cards ${context.username} has up for trade. Tell us what you're after and we'll match you with their listings.`
        : `Cards ${context.username} is looking for. List the ones you have and we'll connect you.`;
    case "set":
      return isHaves
        ? `Cards from ${context.setName} being traded by players. Tell us what you're after and we'll match you with traders nearby.`
        : `Pick the ${context.setName} cards you want. We'll match you with players nearby who have them up for trade.`;
  }
}

/**
 * Button label for the mobile <VisitorCtaBar>. Owner-anchored: a visitor
 * who tapped a shared link arrived with one person or place in mind, so
 * the button names it instead of a generic "Start trading".
 */
export function buildFunnelCtaLabel(context: FunnelContext): string {
  switch (context.kind) {
    case "user":
      return `Trade with ${context.username}`;
    case "list":
      return `Trade with ${context.ownerName}`;
    case "city":
      return `Trade in ${context.cityName}`;
    case "store":
      return `Trade at ${context.storeName}`;
    case "set":
      return `Find ${context.setName} cards`;
  }
}
